/**
 * Reward Node Validator
 * Validates all reward node types (POI, TextClue, Audio, Video, Image, etc.)
 */

import { WorkflowNode } from '@store/workflowStore'
import { WorkflowNodeType } from '@features/adventures/types/workflow.types'
import {
  ValidationIssue,
  ValidationSeverity,
  ValidationCategory,
  ValidationErrorCodes,
} from '@features/adventures/types/validation.types'
import { createIssue } from './helpers'

/**
 * Validate a POI Location node
 */
function validatePoiLocation(node: WorkflowNode): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const config = node.data.poiLocationConfig

  if (!config) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_POI_LOCATION,
      message: 'POI Location must be configured',
    }))
    return issues
  }

  // Validate coordinates
  const lat = config.latitude
  const lng = config.longitude

  const isValidLat = typeof lat === 'number' && lat >= -90 && lat <= 90
  const isValidLng = typeof lng === 'number' && lng >= -180 && lng <= 180
  const isDefaultLocation = lat === 0 && lng === 0

  if (!isValidLat || !isValidLng || isDefaultLocation) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.INVALID_COORDINATES,
      message: 'POI must have valid latitude and longitude',
      details: isDefaultLocation ? 'Location appears to be unset (0,0)' : undefined,
    }))
  }

  return issues
}

/**
 * Validate a Clue Gate node (formerly Text Clue)
 */
function validateTextClue(node: WorkflowNode): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const clueGateConfig = node.data.clueGateConfig
  const textClueConfig = node.data.textClueConfig

  // ClueGate with no clues is valid — it clears all allowed clues at that point.
  // Legacy TextClue fallback: warn if no message configured.
  if (!clueGateConfig && (!textClueConfig?.message || textClueConfig.message.trim().length === 0)) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Warning,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_CLUE_IDS,
      message: 'Clue Gate has no clues selected. Configure clues in the node properties.',
    }))
  }

  return issues
}

/**
 * Validate an Audio Player node
 */
function validateAudioPlayer(node: WorkflowNode): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const config = node.data.audioConfig

  if (!config?.mediaFileId && !config?.audioUrl) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_AUDIO_FILE,
      message: 'Audio Player must have an audio file',
    }))
  }

  return issues
}

/**
 * Validate a Video Player node
 */
function validateVideoPlayer(node: WorkflowNode): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const config = node.data.videoConfig

  if (!config?.videoUrl || config.videoUrl.trim().length === 0) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_VIDEO_URL,
      message: 'Video Player must have a video URL',
    }))
  }

  return issues
}

/**
 * Validate an Image Display node
 */
function validateImageDisplay(node: WorkflowNode): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const config = node.data.imageConfig

  if (!config?.mediaFileId && !config?.imageUrl) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_IMAGE,
      message: 'Image Display must have an image',
    }))
  }

  return issues
}

/**
 * Validate an Unlock Adventure node
 */
function validateUnlockAdventure(node: WorkflowNode): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const config = node.data.unlockAdventureConfig

  if (!config?.adventureId) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_ADVENTURE,
      message: 'Unlock Adventure must have an adventure selected',
    }))
  }

  return issues
}

/**
 * Validate a Time Modifier (Bonus/Penalty) node
 */
function validateTimeModifier(node: WorkflowNode): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const timeBonusSeconds = node.data.timeBonusSeconds

  if (timeBonusSeconds === undefined || timeBonusSeconds === null || timeBonusSeconds === 0) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.INVALID_TIME_VALUE,
      message: 'Time Bonus/Penalty must have a non-zero time value',
    }))
  }

  return issues
}

/**
 * Validate a Formatted Message node
 */
function validateFormattedMessage(node: WorkflowNode): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const config = node.data.formattedMessageConfig

  // Strip HTML tags for length check
  const htmlContent = config?.htmlContent || ''
  const textContent = htmlContent.replace(/<[^>]*>/g, '').trim()

  if (textContent.length < 25) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MESSAGE_TOO_SHORT,
      message: 'Formatted Message must have at least 25 characters of content',
      details: `Current length: ${textContent.length}`,
    }))
  }

  return issues
}

/**
 * Validate a Group Membership node
 */
function validateGroupMembership(node: WorkflowNode): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const config = node.data.groupMembershipConfig

  if (!config?.groupId) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_GROUP,
      message: 'Group Membership must have a group selected',
    }))
  }

  return issues
}

/**
 * Validate a Gift Reward node
 */
function validateGiftReward(node: WorkflowNode): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const config = node.data.giftRewardConfig

  if (!config?.templateId) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_GIFT_REWARD_TEMPLATE,
      message: 'Gift Reward must have a reward template selected',
    }))
  }

  return issues
}

/**
 * Validates all reward nodes in the workflow
 */
export function validateRewardNodes(nodes: WorkflowNode[]): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  for (const node of nodes) {
    switch (node.data.nodeType) {
      case WorkflowNodeType.PoiLocation:
        issues.push(...validatePoiLocation(node))
        break
      case WorkflowNodeType.TextClue:
        issues.push(...validateTextClue(node))
        break
      case WorkflowNodeType.AudioPlayer:
        issues.push(...validateAudioPlayer(node))
        break
      case WorkflowNodeType.VideoPlayer:
        issues.push(...validateVideoPlayer(node))
        break
      case WorkflowNodeType.ImageDisplay:
        issues.push(...validateImageDisplay(node))
        break
      case WorkflowNodeType.UnlockAdventure:
        issues.push(...validateUnlockAdventure(node))
        break
      case WorkflowNodeType.TimeModifier:
        issues.push(...validateTimeModifier(node))
        break
      case WorkflowNodeType.FormattedMessage:
        issues.push(...validateFormattedMessage(node))
        break
      case WorkflowNodeType.GroupMembership:
        issues.push(...validateGroupMembership(node))
        break
      case WorkflowNodeType.GiftReward:
        issues.push(...validateGiftReward(node))
        break
    }
  }

  return issues
}
